/**
 * MODULE: _shared/frontend-shell/src/pages/activity/useUnreadSignals.ts
 * PURPOSE: Unread sales signal count for mobile Activity tab dot (Sprint 11).
 * DEPENDENCIES: react-router-dom, @shared/lib/frontend/api/dealIntelligenceApi, gatewayRateLimit
 * PUBLIC_API: useUnreadSignals, markSignalsSeen
 * SIDE_EFFECTS: localStorage brazn.activity.signals.lastSeenAt; HTTP GET signals
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-05-24 [2.2.1137] — limit 20, 60s dedupe, gateway backoff guard; pairs with dealIntelligenceApi UAT mock.
 */

import { listTenantSignals } from '@shared/lib/frontend/api/dealIntelligenceApi';
import { isInGatewayBackoff } from '@shared/lib/frontend/api/gatewayRateLimit';
import {
  readApiAuthReady,
  useSessionAuthReady,
} from '@shared/lib/frontend/api/sessionAuthReady';
import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'brazn.activity.signals.lastSeenAt';
/** Enough for unread badge; avoids heavy limit=100 gateway load. */
const UNREAD_SIGNALS_LIMIT = 20;
/** Dedupe Strict Mode remounts and mobile nav re-renders within one minute. */
const UNREAD_SIGNALS_REFRESH_MS = 60_000;

let lastUnreadSignalsFetchAt = 0;
let unreadSignalsInflight: Promise<void> | null = null;

/** Test-only: reset module-level fetch dedupe between Vitest files (Sprint 11 gate). */
export function __test_resetUnreadSignalsFetchState(): void {
  lastUnreadSignalsFetchAt = 0;
  unreadSignalsInflight = null;
}

export function markSignalsSeen(): void {
 if (typeof window === 'undefined') return;
 window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
}

function readLastSeen(): string | null {
 if (typeof window === 'undefined') return null;
 return window.localStorage.getItem(STORAGE_KEY);
}

function isActivityPath(pathname: string): boolean {
 return (
 pathname === '/activity' ||
 pathname.startsWith('/activity/') ||
 pathname === '/activities' ||
 pathname.startsWith('/activities/')
 );
}

export function useUnreadSignals() {
 const { pathname } = useLocation();
 const [unreadCount, setUnreadCount] = useState(0);

 const apiReady = useSessionAuthReady();

 const refresh = useCallback(async () => {
 if (!readApiAuthReady()) return;
 if (isInGatewayBackoff()) return;
 if (Date.now() - lastUnreadSignalsFetchAt < UNREAD_SIGNALS_REFRESH_MS) return;
 if (unreadSignalsInflight) {
 await unreadSignalsInflight;
 return;
 }

 unreadSignalsInflight = (async () => {
 try {
 const signals = await listTenantSignals({ limit: UNREAD_SIGNALS_LIMIT });
 lastUnreadSignalsFetchAt = Date.now();
 const lastSeen = readLastSeen();
 if (!lastSeen) {
 setUnreadCount(signals.length);
 return;
 }
 const seenAt = new Date(lastSeen).getTime();
 const count = signals.filter((s) => new Date(s.detectedAt).getTime() > seenAt).length;
 setUnreadCount(count);
 } catch {
 setUnreadCount(0);
 } finally {
 unreadSignalsInflight = null;
 }
 })();

 await unreadSignalsInflight;
 }, []);

 useEffect(() => {
 if (!apiReady) return;
 void refresh();
 }, [refresh, apiReady]);

 useEffect(() => {
 if (isActivityPath(pathname)) {
 markSignalsSeen();
 setUnreadCount(0);
 }
 }, [pathname]);

 return { unreadCount, refresh };
}
